/**
 * ProfileHeader - User avatar, name, and edit button
 * With client-side compression, optimistic preview, progress indicator
 */
import { useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, User, Pencil, Camera, Loader2, RefreshCw } from "lucide-react";
import { FoundingBadgeInline } from "@/components/FoundingBadge";
import { toast } from "sonner";
import { pickImage, isNativeApp } from "@/utils/nativeBridge";
import { compressImage, validateImageFile, createPreviewUrl } from "@/utils/imageCompression";
import { API } from "@/App";
import axios from "axios";

const ProfileHeader = ({ user, onEditProfile, onAvatarUpdated }) => {
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadFailed, setUploadFailed] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);
  const pendingUploadRef = useRef(null);

  const uploadBase64 = useCallback(async (base64, filename, contentType) => {
    setUploadFailed(false);
    setProgress(10);

    const response = await axios.post(
      `${API}/profile/picture/upload-base64`,
      { image: base64, filename, content_type: contentType },
      {
        timeout: 30000,
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 90) + 10);
        },
      }
    );

    setProgress(100);
    return response;
  }, []);

  const handleUpload = useCallback(async (base64, filename, contentType, localPreview) => {
    pendingUploadRef.current = { base64, filename, contentType, localPreview };
    setUploadingAvatar(true);
    setUploadFailed(false);

    try {
      const response = await uploadBase64(base64, filename, contentType);
      if (response.data.success) {
        toast.success("Profile picture updated!");
        if (onAvatarUpdated) onAvatarUpdated();
      }
      pendingUploadRef.current = null;
    } catch (error) {
      console.error("Upload failed:", error);
      setUploadFailed(true);
      toast.error("Upload failed. Tap retry to try again.", { duration: 5000 });
    } finally {
      setUploadingAvatar(false);
      setProgress(0);
    }
  }, [uploadBase64, onAvatarUpdated]);

  const handleRetry = useCallback(() => {
    const pending = pendingUploadRef.current;
    if (pending) {
      handleUpload(pending.base64, pending.filename, pending.contentType, pending.localPreview);
    }
  }, [handleUpload]);

  const handleAvatarClick = async () => {
    if (uploadingAvatar) return;

    if (isNativeApp()) {
      try {
        const image = await pickImage({ allowsEditing: true, aspect: [1, 1], quality: 0.8 });
        if (!image) return;

        // Optimistic preview
        if (image.uri) setPreviewUrl(image.uri);

        // Compress
        setProgress(5);
        const compressed = await compressImage(image.base64);
        handleUpload(compressed.base64, image.fileName || 'profile.jpg', 'image/jpeg', image.uri);
      } catch (error) {
        if (error.message !== 'Image picker timeout') {
          toast.error("Failed to pick image");
        }
        setPreviewUrl(null);
      }
    } else {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const validation = validateImageFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    // Optimistic preview instantly
    const localUrl = createPreviewUrl(file);
    setPreviewUrl(localUrl);

    try {
      setProgress(5);
      const compressed = await compressImage(file);
      handleUpload(compressed.base64, file.name || 'profile.jpg', 'image/jpeg', localUrl);
    } catch (error) {
      console.error("Compression failed:", error);
      toast.error("Failed to process image");
      setPreviewUrl(null);
    }
  };

  // Cache-busted avatar URL
  const avatarSrc = previewUrl
    || (user.profile_picture_path
        ? `${API}/profile/picture/${user.profile_picture_path}?v=${Date.now()}`
        : user.preferences?.avatar_url)
    || null;

  return (
    <div className="bg-card border-b border-charcoal/5">
      <input
        type="file"
        ref={fileInputRef}
        accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif"
        onChange={handleFileChange}
        className="hidden"
        data-testid="header-avatar-file-input"
      />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-charcoal transition-colors mb-6"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>

        <div className="flex items-center gap-4">
          {/* Avatar with upload */}
          <div className="relative group">
            {avatarSrc ? (
              <img
                src={avatarSrc}
                alt={user.name}
                className={`w-16 h-16 rounded-full object-cover border-2 border-primary/20 ${user.streak_frame_enabled ? 'streak-flame-frame' : ''}`}
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="w-8 h-8 text-primary" />
              </div>
            )}

            {/* Progress ring */}
            {uploadingAvatar && progress > 0 && (
              <svg className="absolute inset-0 w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                <circle cx="32" cy="32" r="30" fill="none" stroke="rgba(224,122,95,0.3)" strokeWidth="3" />
                <circle
                  cx="32" cy="32" r="30" fill="none"
                  stroke="#E07A5F" strokeWidth="3"
                  strokeDasharray={`${progress * 1.885} 188.5`}
                  strokeLinecap="round"
                  className="transition-all duration-300"
                />
              </svg>
            )}

            <button
              onClick={uploadFailed ? handleRetry : handleAvatarClick}
              disabled={uploadingAvatar}
              className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-70 disabled:cursor-wait"
              data-testid="change-avatar-btn"
              title={uploadFailed ? "Retry upload" : "Change profile picture"}
            >
              {uploadingAvatar ? (
                <Loader2 className="w-5 h-5 text-white animate-spin" />
              ) : uploadFailed ? (
                <RefreshCw className="w-5 h-5 text-white" />
              ) : (
                <Camera className="w-5 h-5 text-white" />
              )}
            </button>
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-serif text-2xl font-medium text-charcoal">{user.name}</h1>
              {user.founding_explorer && (
                <FoundingBadgeInline
                  founderNumber={user.founding_explorer_number}
                  size="xs"
                  showNumber={false}
                />
              )}
              {user.streak_legend_title && (
                <span className="text-xs px-2 py-0.5 bg-pink-500/20 text-pink-500 rounded-full font-medium">Legend</span>
              )}
              {user.ambassador_level && (
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  user.ambassador_level === "gold" ? "bg-yellow-500/20 text-yellow-600" :
                  user.ambassador_level === "silver" ? "bg-slate-300/20 text-slate-500" :
                  "bg-amber-500/20 text-amber-600"
                }`}>{user.ambassador_level === "gold" ? "Gold" : user.ambassador_level === "silver" ? "Silver" : ""} Ambassador</span>
              )}
            </div>
            <p className="text-muted-foreground">{user.email}</p>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={onEditProfile}
            className="rounded-xl"
            data-testid="edit-profile-btn"
          >
            <Pencil className="w-4 h-4 mr-1" />
            Edit
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;
